import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Search, Users, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { StockPanel } from "./Distributors";
import ExportExcelButton from "@/components/ExportExcelButton";

const FIELDS = [
  { name: "name", label: "Dealer Name" },
  { name: "gst_no", label: "GST Number" },
  { name: "phone", label: "Phone (10-digit)" },
  { name: "email", label: "Email" },
  { name: "address_street", label: "Street", col: 2 },
  { name: "address_city", label: "City" },
  { name: "address_state", label: "State" },
  { name: "address_pin", label: "PIN Code" },
];

export default function Dealers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [distributors, setDistributors] = useState([]);
  const [form, setForm] = useState(Object.fromEntries(FIELDS.map(f => [f.name, ""])));
  const [distributorId, setDistributorId] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [stockByDealer, setStockByDealer] = useState({});
  const [stockLoading, setStockLoading] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/dealers${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleActivation = async (id, currentActive) => {
    const newState = !(currentActive ?? true);
    if (!window.confirm(newState ? "Reactivate this dealer?" : "Deactivate this dealer? They won't be able to log in.")) return;
    try {
      await api.put(`/dealers/${id}/activate`, { is_active: newState });
      toast.success(newState ? "Dealer activated" : "Dealer deactivated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Toggle failed"); }
  };

  const toggleExpand = async (d) => {
    if (expandedId === d.id) { setExpandedId(null); return; }
    setExpandedId(d.id);
    if (!stockByDealer[d.user_id]) {
      setStockLoading((s) => ({ ...s, [d.user_id]: true }));
      try {
        const r = await api.get(`/dealers/${d.user_id}/stock`);
        setStockByDealer((prev) => ({ ...prev, [d.user_id]: r.data || [] }));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load stock");
        setStockByDealer((prev) => ({ ...prev, [d.user_id]: [] }));
      } finally {
        setStockLoading((s) => ({ ...s, [d.user_id]: false }));
      }
    }
  };

  useEffect(() => {
    if (user?.role === "manufacturer" && open) {
      api.get("/distributors?status=approved").then(r => setDistributors(r.data));
    }
  }, [open, user]);

  const submit = async (e) => {
    e.preventDefault();
    for (const f of FIELDS) if (!form[f.name]) { toast.error(`${f.label} required`); return; }
    if (user?.role === "manufacturer" && !distributorId) { toast.error("Select distributor"); return; }
    try {
      const payload = { ...form };
      if (user?.role === "manufacturer") payload.distributor_id = distributorId;
      await api.post("/dealers", payload);
      toast.success("Dealer created");
      setOpen(false);
      setForm(Object.fromEntries(FIELDS.map(f => [f.name, ""])));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#64748B]">Network</div>
          <h1 className="text-3xl tracking-tighter font-medium mt-1">Dealers</h1>
        </div>
        <button
          data-testid="create-dealer-btn"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md text-sm font-medium"
        >
          <Plus size={14} /> New Dealer
        </button>
      </div>

      <div className="flex justify-end">
        <ExportExcelButton path="/export/dealers" filename="dealers.xlsx"
          testid="export-dealers-btn" />
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
        <input
          data-testid="dealer-search"
          value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search dealers..."
          className="w-full pl-9 pr-3 py-2 text-sm bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none focus:border-[#E63946]"
        />
      </div>

      {loading ? (
        <div className="text-sm text-[#475569]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-[#64748B] border border-dashed border-[#E5E7EB] rounded-xl">
          <Users size={28} className="mx-auto mb-3 text-[#64748B]" />
          No dealers yet. Create one to get started.
        </div>
      ) : (
        <div className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] overflow-hidden">
          <table className="w-full text-sm" data-testid="dealers-table">
            <thead>
              <tr className="border-b border-[#E5E7EB] text-left">
                <th className="px-3 py-3 w-8"></th>
                {["Dealer", "GST", "Phone", "City", "State", "Created", "Actions"].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <React.Fragment key={d.id}>
                  <tr className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC]">
                    <td className="px-3 py-3">
                      <button data-testid={`expand-dealer-${d.id}`} onClick={() => toggleExpand(d)}
                        className="p-1 rounded hover:bg-[#E5E7EB]/60 text-[#64748B]">
                        {expandedId === d.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{d.name}</div>
                      <div className="text-[11px] text-[#64748B]">{d.email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#475569]">{d.gst_no}</td>
                    <td className="px-4 py-3 font-mono text-xs">+91 {d.phone}</td>
                    <td className="px-4 py-3 text-xs">{d.address_city}</td>
                    <td className="px-4 py-3 text-xs">{d.address_state}</td>
                    <td className="px-4 py-3 text-xs text-[#64748B]">{fmt(d.created_at)}</td>
                    <td className="px-4 py-3">
                      <button data-testid={`toggle-dealer-${d.id}`}
                        onClick={() => toggleActivation(d.id, d.is_active)}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border ${
                          (d.is_active ?? true)
                            ? "bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/30 hover:bg-[#EF4444]/20"
                            : "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30 hover:bg-[#10B981]/20"
                        }`}>
                        {(d.is_active ?? true) ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                  {expandedId === d.id && (
                    <tr className="bg-[#F8FAFC]/60" data-testid={`dealer-stock-${d.id}`}>
                      <td colSpan={8} className="px-6 py-4">
                        <StockPanel
                          loading={!!stockLoading[d.user_id]}
                          rolls={stockByDealer[d.user_id] || []}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
              <h3 className="text-lg font-medium">Create Dealer</h3>
              <button onClick={() => setOpen(false)} className="text-[#475569] hover:text-slate-900" data-testid="dealer-modal-close">✕</button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              {user?.role === "manufacturer" && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Assign to Distributor</label>
                  <select
                    data-testid="dealer-distributor-select"
                    value={distributorId} onChange={(e) => setDistributorId(e.target.value)}
                    className="mt-1 w-full px-3 py-2.5 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none focus:border-[#E63946]"
                  >
                    <option value="">Select…</option>
                    {distributors.map((d) => (
                      <option key={d.id} value={d.user_id}>{d.name} ({d.address_city})</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                {FIELDS.map(f => (
                  <div key={f.name} className={f.col === 2 ? "sm:col-span-2" : ""}>
                    <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">{f.label}</label>
                    <input
                      data-testid={`dealer-${f.name}`}
                      value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      className="mt-1 w-full px-3 py-2.5 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none focus:border-[#E63946]"
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-md hover:bg-[#E5E7EB]">Cancel</button>
                <button data-testid="dealer-submit" type="submit" className="px-5 py-2 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md text-sm font-medium">Create Dealer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
function fmt(d) { if (!d) return "-"; try { return new Date(d).toLocaleDateString("en-IN"); } catch { return d; } }
